import { Loader2, Sparkles, X } from 'lucide-react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useForm } from 'react-hook-form';

import FadeIn from '@/components/animations/FadeIn';
import { CoursePullPanel } from '@/components/CoursePullPanel/CoursePullPanel';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import useAiSuggest from '@/hooks/useAiSuggest';
import { useTabLabels } from '@/hooks/useTabLabels';
import { skillCompositeKey } from '@/lib/utils';

import CourseEditContext from '../CourseEditContext';

import CourseUnitsForm from './CourseUnitsForm';
import CourseUnitsPlaceholder from './CourseUnitsPlaceholder';

const CourseUnitsTab = () => {
	const {
		addFocus,
		addFocusWithTitle,
		importFocusFromPull,
		courseData,
		courseDraftData,
		isApprovalMode,
		canEdit,
		submitCourse,
		updateFocus,
	} = useContext(CourseEditContext);

	const { handleSubmit, register, watch, getValues } = useForm({
		shouldUnregister: true,
	});
	const labels = useTabLabels();

	const handleFocusUpdate = () => {
		const formValues = watch();
		const entries = Object.entries(formValues);

		// Only process skill-related changes, not title changes
		// Title changes are handled by the debounced updateUnitTitle function
		const hasSkillChanges = entries.some(
			([key]) => key.includes('skillBlob') || key.includes('skill-')
		);

		if (!hasSkillChanges) {
			return; // Skip if no skill changes
		}

		// First pass: collect focuses and skills
		const focuses = new Map();
		const skills = new Map();

		entries.forEach(([key, value]) => {
			if (key.includes('focusBlob')) {
				const { courseFocusId, focusId, focusOrder } = JSON.parse(value);
				const title =
					courseDraftData.courses_focuses?.find(
						(focus) => focus.courses_focuses_id === courseFocusId
					)?.focus?.focus_title ?? '';

				focuses.set(courseFocusId, {
					courses_focuses_id: courseFocusId,
					focus: {
						focus_id: focusId,
						focus_order: +focusOrder,
						focus_type: '',
						focus_title: title,
						focuses_skills: [],
					},
				});
			}

			if (key.includes('skillBlob')) {
				const { focusSkillId, skillText, skillId, skillType, parentId } = JSON.parse(value);
				if (!skills.has(parentId)) {
					skills.set(parentId, []);
				}
				skills.get(parentId).push({
					focuses_skills_id: focusSkillId,
					skill: {
						skill_id: skillId,
						skill_text: getValues(skillText),
						skill_type: getValues(skillType),
					},
				});
			}
		});

		// Second pass: combine focuses with their skills
		const focusArray = Array.from(focuses.values()).map((focus) => {
			const focusSkills = skills.get(focus.courses_focuses_id) ?? [];
			return {
				...focus,
				focus: {
					...focus.focus,
					focuses_skills: focusSkills,
				},
			};
		});

		updateFocus(focusArray);
	};

	const inputFocusRef = useRef(false);

	const {
		suggest: generateUnits,
		loading: unitsAiLoading,
		suggestions: aiSuggestions,
		panelOpen: unitsAiPanelOpen,
		setPanelOpen: setUnitsAiPanelOpen,
		markAccepted,
		error: aiError,
	} = useAiSuggest('generate_units');

	const [checkedUnits, setCheckedUnits] = useState<Record<number, boolean>>({});
	const [pullPanelOpen, setPullPanelOpen] = useState(false);

	const existingFocusTitles = useMemo(
		() =>
			(courseDraftData.courses_focuses ?? [])
				.map((f: CourseFocusEntity) => f.focus?.focus_title?.trim() ?? '')
				.filter(Boolean),
		[courseDraftData.courses_focuses]
	);

	const existingSkillKeys = useMemo(() => {
		const set = new Set<string>();
		for (const f of courseDraftData.courses_focuses ?? []) {
			for (const fs of f.focus?.focuses_skills ?? []) {
				const text = fs.skill?.skill_text?.trim() ?? '';
				if (!text) continue;
				const st = fs.skill?.skill_type ?? '';
				set.add(skillCompositeKey(st, text));
			}
		}
		return set;
	}, [courseDraftData.courses_focuses]);

	useEffect(() => {
		const initial: Record<number, boolean> = {};
		aiSuggestions.forEach((_, i) => {
			initial[i] = true;
		});
		setCheckedUnits(initial);
	}, [aiSuggestions]);

	const handleAddSelected = () => {
		aiSuggestions.forEach((text, i) => {
			if (checkedUnits[i] && typeof text === 'string') {
				addFocusWithTitle(text);
			}
		});
		markAccepted();
		setUnitsAiPanelOpen(false);
	};

	return (
		<FadeIn style={{ width: '100%' }}>
			<div style={{ width: '100%' }}>
				{/* Tab-level AI: Generate Units */}
				{canEdit && !isApprovalMode && (
					<div className="flex justify-end gap-2 mb-4 flex-wrap">
						<Button
							type="button"
							variant="secondary"
							size="xs"
							onClick={() => setPullPanelOpen(true)}
							disabled={!courseData?.course_id}
							aria-label={`Copy ${labels.units.toLowerCase()} from another course at your school into this course`}
						>
							Copy from another course
						</Button>
						<Button
							type="button"
							variant="secondary"
							size="xs"
							onClick={generateUnits}
							disabled={unitsAiLoading}
						>
							{unitsAiLoading ? (
								<Loader2 size={12} className="animate-spin mr-0.5" />
							) : (
								<Sparkles size={12} className="mr-0.5" />
							)}
							{unitsAiLoading ? 'Generating…' : 'Generate Units'}
						</Button>
					</div>
				)}

				{/* AI error */}
				{aiError && <p className="text-[12px] text-destructive mb-4">{aiError}</p>}

				{pullPanelOpen && courseData?.course_id && (
					<CoursePullPanel
						mode="focuses"
						currentCourseId={courseData.course_id}
						existingTexts={existingFocusTitles}
						existingSkillKeys={existingSkillKeys}
						skillTypeLabels={labels}
						label={labels.units}
						onAdd={(items) => {
							for (const item of items) {
								importFocusFromPull(item);
							}
						}}
						onClose={() => setPullPanelOpen(false)}
					/>
				)}

				{/* AI suggestion panel */}
				{unitsAiPanelOpen && (
					<div className="rounded-lg border border-border bg-secondary/40 p-4 mb-5">
						<div className="flex items-center justify-between mb-3">
							<div className="flex items-center gap-2 text-[13.5px] font-semibold text-primary">
								<Sparkles size={13} />
								AI-suggested {labels.units.toLowerCase()}
								<span className="text-[12px] font-normal text-muted-foreground">
									Based on your course description
								</span>
							</div>
							<div className="flex items-center gap-2">
								<Button type="button" size="xs" onClick={handleAddSelected}>
									Add selected
								</Button>
								<button
									type="button"
									onClick={() => setUnitsAiPanelOpen(false)}
									className="text-muted-foreground hover:text-foreground transition-colors p-1"
									aria-label="Discard suggestions"
								>
									<X size={14} />
								</button>
							</div>
						</div>
						<div className="flex flex-col gap-1.5">
							{aiSuggestions.map((text, i) => (
								<button
									key={typeof text === 'string' ? text : i}
									type="button"
									onClick={() => setCheckedUnits((prev) => ({ ...prev, [i]: !prev[i] }))}
									className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border text-left text-[13px] transition-colors ${
										checkedUnits[i]
											? 'border-border bg-white text-foreground'
											: 'border-transparent bg-white/50 text-muted-foreground'
									}`}
								>
									<Checkbox
										checked={!!checkedUnits[i]}
										className="mt-0.5 shrink-0"
										onCheckedChange={() => setCheckedUnits((prev) => ({ ...prev, [i]: !prev[i] }))}
									/>
									{typeof text === 'string' ? text : ''}
								</button>
							))}
						</div>
					</div>
				)}

				<CourseUnitsPlaceholder
					isApprovalMode={isApprovalMode}
					coursesDraftFocuses={courseDraftData.courses_focuses ?? []}
				/>
				<form onSubmit={handleSubmit(submitCourse)} onChange={handleFocusUpdate}>
					<CourseUnitsForm inputFocusRef={inputFocusRef} register={register} />
				</form>
			</div>
			{canEdit && !isApprovalMode && (
				<div className="actions-container">
					<Button variant="secondary" size="small" type="button" onClick={addFocus}>
						Add {labels.units}
					</Button>
				</div>
			)}
		</FadeIn>
	);
};

export default CourseUnitsTab;
